import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calendar, User, Tag, ArrowRight, Newspaper, Share2, Mail } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { BACKEND_URL } from "../context/AuthContext";

const CATEGORIES = ["All", "Platform Updates", "DJ Tips", "Industry News", "Artist Spotlights", "Station Features"];

export default function NewsPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [subEmail, setSubEmail] = useState('');
  const [subMsg, setSubMsg] = useState('');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/news?status=published`)
      .then((r) => r.json())
      .then((data) => setPosts(Array.isArray(data) ? data : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeCategory === "All" ? posts : posts.filter((p) => p.category === activeCategory);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white">
      <SEO
        title="News"
        description="Latest news, updates, and articles from DJsandMCMedia™. Platform updates, DJ tips, industry news, and artist spotlights."
      />
      <Header />

      {/* Hero Section with Video Background */}
      <section className="relative overflow-hidden" style={{ minHeight: "420px" }}>
        <video
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="news-hero-video"
        >
          <source src="/news-hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,20,0.1) 0%, rgba(10,10,20,0.9) 100%)" }} />

        <div className="relative z-10 max-w-5xl mx-auto px-6 pt-28 pb-16 text-center">
          <div className="inline-flex items-center gap-2 bg-purple-500/15 border border-purple-500/25 px-4 py-1.5 rounded-full text-purple-300 text-sm mb-6 backdrop-blur-sm">
            <Newspaper className="w-4 h-4" /> Latest Updates
          </div>
          <h1 data-testid="news-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight drop-shadow-lg">Culture. Sound. Conversation.</h1>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto drop-shadow">
            Platform updates, DJ tips, industry insights, and artist spotlights.
          </p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 py-10">

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 justify-center mb-10" data-testid="news-categories">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                activeCategory === cat
                  ? "bg-purple-500 text-white"
                  : "bg-white/5 text-white/60 hover:bg-white/10"
              }`}
              data-testid={`news-cat-${cat.toLowerCase().replace(/\s/g, "-")}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-white/20 border-t-purple-500 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Newspaper className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white/40 mb-2">No articles yet</h2>
            <p className="text-white/30 text-sm">Check back soon for news and updates.</p>
          </div>
        ) : (
          <>
            {/* Featured Post */}
            {featured && (
              <div
                onClick={() => navigate(`/news/${featured.slug}`)}
                data-testid="news-featured"
                className="block mb-10 group rounded-2xl overflow-hidden border border-white/[0.08] hover:border-purple-500/20 transition-all cursor-pointer"
              >
                <div className="grid md:grid-cols-2">
                  <div className="aspect-video md:aspect-auto bg-[#1a1a2e] overflow-hidden">
                    {featured.featured_image ? (
                      <img src={featured.featured_image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center min-h-[240px]">
                        <Newspaper className="w-16 h-16 text-white/10" />
                      </div>
                    )}
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <span className="text-purple-400 text-xs font-semibold uppercase tracking-wider mb-2">{featured.category}</span>
                    <h2 className="text-2xl font-bold mb-3 group-hover:text-purple-400 transition">{featured.title}</h2>
                    <p className="text-white/50 text-sm mb-4 line-clamp-3">{featured.excerpt || featured.body?.slice(0, 200)}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-white/30 text-xs">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{featured.author}</span>
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(featured.created_at).toLocaleDateString()}</span>
                      </div>
                      <button onClick={(e) => {
                        e.stopPropagation();
                        const url = `${window.location.origin}/news/${featured.slug}`;
                        if (navigator.share) { navigator.share({ title: featured.title, url }); }
                        else { navigator.clipboard.writeText(url); }
                      }} className="p-2 rounded-lg hover:bg-white/10 text-white/30 hover:text-purple-400 transition" data-testid="news-share-featured" title="Share">
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Post Grid */}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="news-grid">
                {rest.map((post) => (
                  <div
                    key={post.post_id}
                    onClick={() => navigate(`/news/${post.slug}`)}
                    data-testid={`news-card-${post.slug}`}
                    className="group rounded-2xl overflow-hidden border border-white/[0.08] hover:border-purple-500/20 bg-white/[0.02] hover:bg-white/[0.04] transition-all cursor-pointer"
                  >
                    <div className="aspect-video bg-[#1a1a2e] overflow-hidden">
                      {post.featured_image ? (
                        <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Newspaper className="w-10 h-10 text-white/10" />
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <span className="text-purple-400 text-[10px] font-semibold uppercase tracking-wider">{post.category}</span>
                      <h3 className="text-white font-semibold mt-1 mb-2 group-hover:text-purple-400 transition line-clamp-2">{post.title}</h3>
                      <p className="text-white/40 text-sm line-clamp-2 mb-3">{post.excerpt || post.body?.slice(0, 120)}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-white/25 text-xs">
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{post.author}</span>
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(post.created_at).toLocaleDateString()}</span>
                        </div>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          const url = `${window.location.origin}/news/${post.slug}`;
                          if (navigator.share) { navigator.share({ title: post.title, url }); }
                          else { navigator.clipboard.writeText(url); }
                        }} className="p-1.5 rounded-lg hover:bg-white/10 text-white/30 hover:text-purple-400 transition" data-testid={`news-share-${post.slug}`} title="Share">
                          <Share2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Newsletter Subscribe */}
      <section className="max-w-2xl mx-auto px-6 pb-16" data-testid="newsletter-section">
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-8 text-center">
          <Mail className="w-10 h-10 text-purple-400 mx-auto mb-3" />
          <h3 className="text-xl font-bold text-white mb-1">Stay in the loop</h3>
          <p className="text-white/40 text-sm mb-5">Get notified when we publish new articles. No spam, unsubscribe anytime.</p>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!subEmail) return;
            try {
              const res = await fetch(`${BACKEND_URL}/api/news/subscribe`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: subEmail }),
              });
              const data = await res.json();
              setSubMsg(data.message || data.detail);
              if (res.ok) setSubEmail('');
            } catch { setSubMsg('Failed to subscribe. Try again.'); }
            setTimeout(() => setSubMsg(''), 4000);
          }} className="flex gap-2 max-w-md mx-auto">
            <input type="email" required value={subEmail} onChange={(e) => setSubEmail(e.target.value)}
              placeholder="you@email.com" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition text-sm"
              data-testid="newsletter-email" />
            <button type="submit" className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium text-sm transition" data-testid="newsletter-submit">
              Subscribe
            </button>
          </form>
          {subMsg && <p className="text-purple-300 text-sm mt-3">{subMsg}</p>}
        </div>
      </section>

      <Footer />
    </div>
  );
}
